import { useEffect, useRef, useState } from 'react';
import type { LineString } from 'geojson';
import type { SupplierCollectionPoint } from '../types/supplier.type';

interface LatLng {
  latitude: number | null;
  longitude: number | null;
}

/**
 * Free, keyless OSRM demo server - the only zero-config road-routing option available in this
 * stack (no Mapbox/Google Directions key is configured anywhere). Swap this constant if the
 * project later adopts a self-hosted OSRM instance or a paid directions provider.
 */
const OSRM_BASE_URL = 'https://router.project-osrm.org/route/v1/driving';
const DEBOUNCE_MS = 500;

interface RoutedPath {
  geometry: LineString | null;
  distanceKm: number | null;
  durationMinutes: number | null;
  loading: boolean;
  /** True once a real road-following route has been resolved for the current stop list. */
  isRouted: boolean;
  /** True when routing was attempted but failed (network error, no route found) - caller should fall back and say so. */
  failed: boolean;
}

const IDLE: RoutedPath = { geometry: null, distanceKm: null, durationMinutes: null, loading: false, isRouted: false, failed: false };

/**
 * Resolves a real road-following path across `points`, optionally anchored at
 * a start/end location (e.g. a route's start/end facility) so the journey
 * reflects the full trip, not just stop-to-stop.
 */
export function useRoutedPath(
  points: SupplierCollectionPoint[],
  startLocation?: LatLng | null,
  endLocation?: LatLng | null,
): RoutedPath {
  const [result, setResult] = useState<RoutedPath>(IDLE);
  const requestId = useRef(0);
  const startLat = startLocation?.latitude;
  const startLng = startLocation?.longitude;
  const endLat = endLocation?.latitude;
  const endLng = endLocation?.longitude;

  useEffect(() => {
    const locatedStops = points.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
    const start = Number.isFinite(startLat) && Number.isFinite(startLng) ? { latitude: startLat!, longitude: startLng! } : null;
    const end = Number.isFinite(endLat) && Number.isFinite(endLng) ? { latitude: endLat!, longitude: endLng! } : null;
    const located: LatLng[] = [...(start ? [start] : []), ...locatedStops, ...(end ? [end] : [])];
    if (located.length < 2) {
      setResult(IDLE);
      return;
    }

    const id = ++requestId.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setResult((previous) => ({ ...previous, loading: true, failed: false }));
      const coords = located.map((point) => `${point.longitude},${point.latitude}`).join(';');
      fetch(`${OSRM_BASE_URL}/${coords}?overview=full&geometries=geojson`, { signal: controller.signal })
        .then((response) => response.json())
        .then((data) => {
          if (id !== requestId.current) return;
          const route = data?.routes?.[0];
          if (route?.geometry) {
            setResult({
              geometry: route.geometry,
              distanceKm: Math.round((route.distance / 1000) * 10) / 10,
              durationMinutes: Math.round(route.duration / 60),
              loading: false,
              isRouted: true,
              failed: false,
            });
          } else {
            setResult({ ...IDLE, failed: true });
          }
        })
        .catch((error) => {
          if (id !== requestId.current || error?.name === 'AbortError') return;
          setResult({ ...IDLE, failed: true });
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [points, startLat, startLng, endLat, endLng]);

  return result;
}
